// src/components/require-access.tsx — the ONE billing gate for /projects* and
// /editor/*. Calls GET /api/billing/status; renders children only when the
// account has access, otherwise a Renew screen (reason text + /pricing link).
// Also handles the signed-out case (login/signup links) and fetch failures
// (retry screen — the editor is local-first, so we never silently block).

import { useCallback, useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { FreeCutLogo } from '@/components/brand/freecut-logo'
import { Button } from '@/components/ui/button'
import {
  ApiError,
  billingReasonText,
  getBillingStatus,
  getMeOptional,
  type BillingStatus,
} from '@/lib/kwykedit-api'

type GateState =
  | { kind: 'loading' }
  | { kind: 'signed-out' }
  | { kind: 'error'; message: string }
  | { kind: 'denied'; status: BillingStatus }
  | { kind: 'ok' }

function GateShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-12 text-foreground">
      <div className="mb-8">
        <FreeCutLogo variant="full" size="lg" />
      </div>
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 text-center">
        {children}
      </div>
    </div>
  )
}

export function RequireAccess({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GateState>({ kind: 'loading' })

  const check = useCallback(() => {
    setState({ kind: 'loading' })
    void (async () => {
      const me = await getMeOptional().catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 0) throw err
        return null
      })
      if (!me) {
        setState({ kind: 'signed-out' })
        return
      }
      const status = await getBillingStatus()
      setState(status.has_access ? { kind: 'ok' } : { kind: 'denied', status })
    })().catch((err: unknown) => {
      setState({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Could not check subscription status',
      })
    })
  }, [])

  useEffect(() => {
    check()
  }, [check])

  if (state.kind === 'loading') {
    return (
      <GateShell>
        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
        <p className="text-sm text-muted-foreground">Checking your subscription…</p>
      </GateShell>
    )
  }

  if (state.kind === 'signed-out') {
    return (
      <GateShell>
        <h1 className="mb-2 text-xl font-bold">Sign in required</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Log in or create a KWYKEDIT account to use the editor.
        </p>
        <div className="flex flex-col gap-2">
          <Button asChild>
            <Link to="/login">Log in</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/signup">Sign up</Link>
          </Button>
        </div>
      </GateShell>
    )
  }

  if (state.kind === 'error') {
    return (
      <GateShell>
        <h1 className="mb-2 text-xl font-bold">Billing check failed</h1>
        <p className="mb-6 text-sm text-muted-foreground">{state.message}</p>
        <div className="flex flex-col gap-2">
          <Button onClick={check}>Try again</Button>
          <Button asChild variant="outline">
            <Link to="/pricing">View pricing</Link>
          </Button>
        </div>
      </GateShell>
    )
  }

  if (state.kind === 'denied') {
    return (
      <GateShell>
        <h1 className="mb-2 text-xl font-bold">Renew to keep editing</h1>
        <p className="mb-6 text-sm text-muted-foreground">{billingReasonText(state.status)}</p>
        <div className="flex flex-col gap-2">
          <Button asChild>
            <Link to="/pricing">See pricing — $3/mo</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/account">Account details</Link>
          </Button>
        </div>
      </GateShell>
    )
  }

  return <>{children}</>
}
